import {createSelector} from "@reduxjs/toolkit";

import type {RevaLanguage} from "@reva/shared/i18n";
import {formatParameterValue} from "@reva/shared/number-format";
import {groundValueLabel, withGroundValueUnit} from "@reva/shared/standard-ground-value";
import {aggregateTimelineData} from "@reva/shared/timelines";
import type {Feature} from "geojson";

import type {StandardGroundValueItem} from "../api/payload-types/locationinfo-standard-ground-value";
import {selectArg1, selectArg2} from "../util/selectors";
import type {StandardGroundValueParameterData} from "../util/standardGroundValueUtil";
import {findMostFittingGroundValue, findMostFittingGroundValueRaw} from "../util/standardGroundValueUtil";
import {selectPoisData, selectStandardGroundValueData, selectTimelinesData} from "./locationInfoSlice";
import {selectAllDocProperties} from "./objectParametersSlice";
import {
    selectStandardGroundValueInputs,
    selectStandardGroundValueOutputs,
    selectStandardGroundValueSpecModel,
} from "./specSelectors";
import {selectSpec} from "./specSlice";
import type {RevaSelector} from "./store";
import {selectFlattenedIndicationData} from "./valueIndicationSelectors";
import {selectIndicationData} from "./valueIndicationSlice";

/** The POI features the API delivered, each tagged with a synthetic `poi_uid`. */
export interface AggregatedPoisData {
    features: Feature[];
}

/** One row of the ground value table in the PDF; without a value when the field is empty. */
export interface GroundValuePdfRow {
    label: string;
    value?: string;
}

/** The ground value table of the PDF: heading plus one row per spec field. */
export interface GroundValuePdfTable {
    title: string;
    data: GroundValuePdfRow[];
}

/**
 * Selector for the POIs to display.
 *
 * Returns the full POI set the API delivers, each feature tagged with a stable synthetic
 * `poi_uid` in its properties (the API provides no unique identifier and `name` is neither
 * always present nor unique). The API set is Pareto-optimal and can exceed the requested
 * maxRank; the view reduces it per type and vehicle (see `limitPoisPerTypeAndVehicle`).
 */
export const selectPoisDataAggregated = createSelector([selectPoisData], (poisData): AggregatedPoisData | null => {
    if (!poisData || !Array.isArray(poisData.features)) return null;
    return {
        features: poisData.features.map((feature, index) => ({
            ...feature,
            properties: {
                ...feature.properties,
                poi_uid: `poi_${index}`,
            },
        })),
    };
});

/** Selector for Standard Ground Value Items. */
export const selectStandardGroundValues = createSelector([selectStandardGroundValueData], (standardGroundValueData) => {
    if (!standardGroundValueData) return null;
    return standardGroundValueData.list;
});

/** Selector for the most fitting Standard Ground Value item (raw). */
export const selectMostFittingGroundValueRaw: RevaSelector<
    StandardGroundValueItem | null,
    [standardGroundValue: number | null, valuationDate: string | null]
> = createSelector(
    [selectArg1<number | null>, selectArg2<string | null>, selectIndicationData, selectStandardGroundValues],
    (standardGroundValueFromInput, standardGroundValueValuationDateFromInput, indicationData, groundValues) => {
        return findMostFittingGroundValueRaw(
            standardGroundValueFromInput,
            standardGroundValueValuationDateFromInput,
            indicationData,
            groundValues,
        );
    },
);

/** Selector for the most fitting Standard Ground Value parameters. */
export const selectMostFittingGroundValue: RevaSelector<
    Record<string, StandardGroundValueParameterData> | null,
    [standardGroundValue: number | null, valuationDate: string | null]
> = createSelector(
    [
        selectMostFittingGroundValueRaw,
        selectStandardGroundValueSpecModel,
        selectStandardGroundValueInputs,
        selectStandardGroundValueOutputs,
    ],
    (groundValueFromLocationInfo, locInfoSpec, inputSpec, outputSpec) => {
        return findMostFittingGroundValue(groundValueFromLocationInfo, locInfoSpec, inputSpec, outputSpec);
    },
);

export const selectStandardGroundValuesForPdf = createSelector(
    [
        selectArg1<RevaLanguage>,
        selectFlattenedIndicationData,
        selectAllDocProperties,
        selectSpec,
        selectStandardGroundValueSpecModel,
    ],
    (lang, indicationData, docProperties, revaSpec, standardGroundValueSpec): GroundValuePdfTable | null => {
        // the key is the parameter name in the ground value spec, the value is the parameter name of the persisted parameters
        if (!indicationData || !revaSpec || !standardGroundValueSpec) return null;
        const specValueParam = {
            bezGutachterauschusses: "standard_ground_value_source",
            wnum: "standard_ground_value_number",
            stichtag: "standard_ground_value_valuation_date",
            bodenrichtwert: "standard_ground_value",
            entwicklungszustand: "standard_ground_value_entwicklungszustand",
            sanierungUndEntwicklungszusatz: "standard_ground_value_sanierung_und_entwicklungszusatz",
            abgabenrechtlicherZustand: "standard_ground_value_abgabenrechtlicher_zustand",
            nutzungsart: "standard_ground_value_nutzungsart",
            ergNutzungsart: "standard_ground_value_erg_nutzungsart",
            geschosszahl: "standard_ground_value_geschosszahl",
            wertrelevanteGeschossflaechenzahl: "standard_ground_value_floor_area_ratio",
            grundflaechenzahl: "standard_ground_value_grundflaechenzahl",
            grundstueckstiefe: "standard_ground_value_grundstueckstiefe",
            grundstuecksbreite: "standard_ground_value_grundstuecksbreite",
            bauweise: "standard_ground_value_bauweise",
            flaeche: "standard_ground_value_flaeche",
        };
        const stichtag = indicationData["standard_ground_value_valuation_date"];
        const nutzungsart = docProperties?.["standard_ground_value_nutzungsart"];
        const relevantSpec = revaSpec.apiParams.VALUE.EZFH_K;
        const stichtagYear =
            typeof stichtag === "string" || typeof stichtag === "number" ? new Date(stichtag).getFullYear() : "";
        const tableTitle = nutzungsart ? nutzungsart.toString().replace(/(.*).$/, `$1 - ${stichtagYear}`) : "";
        const data: GroundValuePdfRow[] = [];

        const entries = Object.entries(specValueParam);
        entries.forEach(([param, persisted_param]) => {
            const label = groundValueLabel(param, standardGroundValueSpec[param], lang);
            const value = indicationData[persisted_param] ?? docProperties[persisted_param];
            if (!value || value === "empty" || value === -1 || value === 99) {
                data.push({label});
                return;
            }

            const paramDef = relevantSpec[persisted_param] ?? standardGroundValueSpec[param];
            const displayFormat = revaSpec.pages.INCOME_VALUE.rows.find((r) => r.factor?.field === persisted_param)
                ?.factor?.format;
            if ((paramDef.categories && typeof value === "string") || param === "wnum") {
                // #19437: Auch die Grundstücksgröße läuft hier als String durch und braucht ihr m².
                data.push({label, value: withGroundValueUnit(param, value, standardGroundValueSpec[param], lang)});
                return;
            }
            const formattedValue = formatParameterValue(lang, paramDef, value, {formatOverride: displayFormat});
            data.push({
                label,
                value: withGroundValueUnit(param, formattedValue, standardGroundValueSpec[param], lang),
            });
        });

        return {title: tableTitle, data};
    },
);

export const selectTimelineDataAggregated = createSelector([selectTimelinesData], (timelineData) => {
    if (!timelineData) return null;
    return aggregateTimelineData(timelineData);
});
